import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSyncSettings, useSaveSyncSettings } from '@/hooks/useSyncSettings';
import { construirCuerpo, type CambioDeConfiguracion } from './syncPayload';
import { describirFalloAlGuardar } from './saveError';

/**
 * Lo que las tres tarjetas de configuración comparten: leer el documento, guardar un cambio y
 * contar qué pasó.
 *
 * 🔴 **Sin namespace en `useTranslation()`, y es la corrección de un fallo real.** Las claves
 * `jobs.*` viven en el namespace por defecto (`translation`). La tarjeta original pedía
 * `useTranslation('recognition')`, donde no existe ni una clave `jobs`, así que **todos** sus textos
 * caían al valor por defecto del `t()` — español, en los siete idiomas. Traducir las claves sin
 * arreglar esto no habría cambiado nada en pantalla: es la causa de que media pestaña se leyera en
 * español y la otra media en el idioma del usuario.
 */
export function useGuardarConfiguracion() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSyncSettings();
  const save = useSaveSyncSettings();
  const [aviso, setAviso] = useState<string | null>(null);

  /** Guarda un cambio y deja el aviso de éxito (o el error traducido, vía `mensajeDeError`). */
  const guardar = (cambio: CambioDeConfiguracion, avisoDeExito: string, alTerminar?: () => void) => {
    if (!data) return;
    setAviso(null);
    save.mutate(construirCuerpo(data, cambio), {
      onSuccess: () => { setAviso(avisoDeExito); alTerminar?.(); },
    });
  };

  return {
    t,
    data,
    isLoading,
    isError,
    guardando: save.isPending,
    guardar,
    aviso,
    mensajeDeError: save.isError ? describirFalloAlGuardar(save.error, t) : null,
  };
}
